import {
  useGetAccountContractHistoriesQuery,
  useGetAccountOrdersList,
  useGetAccountTPVSQuery,
  useGetAccountBillImagesQuery,
  useGetAccountServiceHistoriesQuery,
} from "@apis/accounts/useAccounts"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@components/ui/select"
import { useState } from "react"
import { Label } from "@components/ui/label"
import type { IAccount } from "@apis/accounts/types"
import type { IOrder } from "@commonTypes/order"
import SalesLocation from "@components/account/serviceSummary/SalesLocation"
import { formatDate, timeAgo } from "@utils/formatters"
import SalesInfo from "./SalesInfo"
import { Loader2 } from "lucide-react"
import EnrollmentDocs from "./EnrollmentDocs"
import { ServiceSummaryInfoSection } from "./ServiceSummaryInfoSection"
import {
  accountBillImagesColumns,
  accountContractHistoriesColumns,
  accountServiceHistoriesColumns,
  accountTPVsColumns,
} from "./utils/tableColumnSchema"

interface IServiceSummaryProps {
  account_id: string
  accountData: IAccount | undefined
}

export default function ServiceSummary({
  account_id,
  accountData,
}: IServiceSummaryProps) {
  const getAccountOrdersList = useGetAccountOrdersList(account_id)

  const [selectedOrderId, setSelectedOrderId] = useState<string>("")

  const firstId =
    getAccountOrdersList.data && getAccountOrdersList.data.length > 0
      ? String(getAccountOrdersList.data[0].order_id)
      : ""

  const orderId = selectedOrderId || firstId
  const selectedOrder: IOrder | null =
    getAccountOrdersList.data?.find(
      (order) => String(order.order_id) === orderId,
    ) ?? null

  const initialEnrollment = Array.isArray(getAccountOrdersList.data) && getAccountOrdersList.data.slice(-1)[0]

  const loader = (
    <div className="tw:flex tw:items-center tw:justify-center tw:p-8">
      <Loader2 className="tw:h-6 tw:w-6 tw:animate-spin" />
    </div>
  )

  return (
    <div className="tw:flex tw:flex-col tw:gap-8">
      <div className="tw:bordered-container tw:flex tw:flex-col tw:gap-4">
        {getAccountOrdersList.isPending ? (
          loader
        ) : getAccountOrdersList.data && getAccountOrdersList.data.length ? (
          <div className="tw:mb-3">
            <Label htmlFor="service-select" className="tw:p-2">
              Signed Up
            </Label>
            <Select value={orderId} onValueChange={setSelectedOrderId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getAccountOrdersList.data.map((order) => (
                  <SelectItem
                    key={order.order_id}
                    value={String(order.order_id)}
                  >
                    {`${formatDate(order.sale_dt)} (${timeAgo(order.sale_dt)})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div>No orders available.</div>
        )}
        {selectedOrder && accountData && (
          <>
            <SalesInfo order={selectedOrder} account={accountData} />
            <SalesLocation order={selectedOrder} />
            <EnrollmentDocs order={selectedOrder} />
          </>
        )}
      </div>

      <div className="tw:bordered-container tw:flex tw:flex-col tw:gap-2">
        <h3>Account Open Info - Initial Enrollment</h3>
        {getAccountOrdersList.isPending ? (
          loader
        ) : getAccountOrdersList.isError ? (
          <p className="tw:text-destructive">
            Error {getAccountOrdersList.error.message}
          </p>
        ) : (
          initialEnrollment && (
            <>
              <p>
                Initial Authorizer:{" "}
                {initialEnrollment.contact_full_name ?? "Unknown"}
              </p>
              <p>
                Contact Relationship:{" "}
                {initialEnrollment.contact_relationship ?? "Unknown"}
              </p>
              <p>Confirmation Code: {initialEnrollment.confirmation_code}</p>
            </>
          )
        )}
      </div>

      {accountData && (
        <div className="tw:bordered-container tw:flex tw:flex-col tw:gap-6">
          <ServiceSummaryInfoSection
            account_id={accountData.account_id}
            queryHook={useGetAccountTPVSQuery}
            columns={accountTPVsColumns}
            title={"TPV"}
          />
          <ServiceSummaryInfoSection
            account_id={accountData.account_id}
            queryHook={useGetAccountContractHistoriesQuery}
            columns={accountContractHistoriesColumns}
            title={"Contracts"}
            initialSorting={[
              { id: "contract_start_dt", desc: true },
              { id: "contract_end_dt", desc: false },
            ]}
          />
          <ServiceSummaryInfoSection
            account_id={accountData.account_id}
            queryHook={useGetAccountBillImagesQuery}
            columns={accountBillImagesColumns}
            title={"Order Bill Images"}
          />
          <ServiceSummaryInfoSection
            account_id={accountData.account_id}
            queryHook={useGetAccountServiceHistoriesQuery}
            columns={accountServiceHistoriesColumns}
            title={"Previous Service Periods"}
          />
        </div>
      )}
    </div>
  )
}
